<template>
    <div class="assistent-message">
        <avatar-icon v-if="isChatStyleFlat" smaller />

        <div class="assistent-message__balloon" v-html="parsedContent" />

        <div
            v-if="isPlaying && !isLoading"
            @keydown="pauseAudio"
            @click="pauseAudio"
        >
            <pause-circle-icon
                v-if="!isError"
                class="assistent-message__audio"
            />
            <error-icon
                v-else
                class="assistent-message__audio"
            />
        </div>
        <div
            v-else-if="!isPlaying && !isLoading"
            @keydown="playAudio"
            @click="playAudio"
        >
            <play-circle-icon
                v-if="!isError"
                class="assistent-message__audio"
            />
            <error-icon
                v-else
                class="assistent-message__audio"
            />
        </div>
        <div
            v-else-if="isLoading"
        >
            <spinner-icon
                class="assistent-message__audio spinner"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import AvatarIcon from '@/components/avatar/AvatarIcon.vue';
    import { computed, onUnmounted, ref } from 'vue';
    import { marked, type Tokens } from 'marked';
    import PlayCircleIcon from '@keyvue/one-awesome-icons/icons/regular/circle-play.svg?component';
    import PauseCircleIcon from '@keyvue/one-awesome-icons/icons/regular/circle-pause.svg?component';
    import SpinnerIcon from '@keyvue/one-awesome-icons/icons/regular/spinner.svg?component';
    import ErrorIcon from '@keyvue/one-material-icons/icons/error.svg?component';
    import useSpeech from '@/composables/useSpeech';
    import useWidgetAppearance from '@/composables/useWidgetAppearance';
    import type Interaction from '@/chat/types/Interaction';

    const props = defineProps<{ interaction: Interaction }>();
    const isPlaying = ref(false);
    const isLoading = ref(false);
    const isError = ref(false);
    const { createSpeech } = useSpeech();
    const { isChatStyleFlat } = useWidgetAppearance();

    const currentAudio = ref<HTMLAudioElement>();

    const parsedContent = computed(() => {
        const localRenderer = new marked.Renderer();

        function renderLink({ href, text }: Tokens.Link) {
            return `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;
        }

        function renderImage({ href, title, text }: Tokens.Image) {
            return `
            <img
                src="${href}"
                alt="${text}"
                title="${title || ''}"
                style="width: 100%; height: auto; object-fit: contain;"
            />
        `;
        }

        localRenderer.link = renderLink;
        localRenderer.image = renderImage;

        return marked(props.interaction.content ?? '', { renderer: localRenderer });
    });

    function onAudioEnded() {
        isPlaying.value = false;
    }

    async function playAudio() {
        isLoading.value = true;

        try {
            const audio = await createSpeech(await parsedContent.value);

            if (audio === null) {
                isLoading.value = false;
                isError.value = true;
                return;
            }

            isError.value = false;
            isLoading.value = false;
            isPlaying.value = !!audio;
            currentAudio.value = audio;

            if (currentAudio.value) {
                await currentAudio.value.play();
                currentAudio.value.addEventListener('ended', onAudioEnded);
            }
        } catch (e) {
            isLoading.value = false;
            isError.value = true;
            throw new Error('Failed to create audio!');
        }
    }

    async function pauseAudio() {
        if (currentAudio.value !== undefined) {
            currentAudio.value.pause();
            isPlaying.value = false;
        }
    }

    onUnmounted(() => {
        if (currentAudio.value !== undefined) {
            currentAudio.value.removeEventListener('ended', onAudioEnded);
        }
    });
</script>

<style scoped lang="scss">
    .assistent-message {
        display: flex;
        align-items: flex-end;
        gap: .5rem;
        min-width: 100px;

        &__balloon {
            background-color: #F5F5F5;
            color: #333;
            padding: 0 1rem;

            :deep(*) {
                font-size: 1rem;
            }

            :deep(ol) {
                list-style-type: auto;
                margin-left: 1rem;
            }

            :deep(ul) {
                list-style-type: disc;
                margin-left: 1rem;
            }

            :deep(li) {
                margin-bottom: .25rem;
            }

            :deep(p), :deep(ol) {
                margin: 1rem 0;
            }

            :deep(strong) {
                font-weight: 700;
                padding-top: 1rem;
            }
        }

        &__audio {
            cursor: pointer;
            width: 25px;
            height: 25px;
            color: var(--color-widget);
        }
    }

    .spinner {
        animation: spin 1s linear infinite;
    }

    .app--flat {
        .assistent-message {
            &__balloon {
                border-radius: .5rem .5rem .5rem 0;
            }
        }
    }

    .app--modern {
        .assistent-message {
            &__balloon {
                border-radius: 0 .5rem .5rem .5rem;
            }
        }
    }

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }

    @media (min-width: 768px) {
        .assistent-message {
            &__balloon {
                &--flat {
                    max-width: 80%;
                }
                &--modern {
                    max-width: 90%;
                }
            }
        }
    }
</style>
